import {
  DoubleColumnLayout,
  DoubleColumnLayoutColumn,
  CustomPricingTableModal,
  SingleColumnLayout,
  BetaPill,
  ElsterLogo,
  LucaIcon
} from '@components'
import {
  FeatureWrapper,
  FlagWrapper,
  isBillableBusiness,
  PermissionBoundary,
  PermissionSet,
  useBusinessContext,
  useFlag,
  useFlagsLoaded,
  usePermission,
  usePermissionsLoaded
} from '@context'
import { getWorkflowsPageUrl } from '@constants'
import { useQueryParam, useQueryParams } from '@hooks'
import { useHistory } from 'react-router-dom'
import { DataExport } from '@pages/SettingsPage/sections/DataExport'
import { DefaultAccountSettings } from '@pages/SettingsPage/sections/AutomationSettings/DefaultAccountSettings'
import { OpeningBalanceSettings } from '@pages/SettingsPage/sections/AutomationSettings/OpeningBalanceSettings'
import { VatSettings } from '@pages/SettingsPage/sections/AutomationSettings/VatSettings'
import AccountingRuleSettings from '@pages/SettingsPage/sections/AutomationSettings/AccountingRule/AccountingRulesSettings'
import InvoiceSettingsAdvanced from '@pages/SettingsPage/sections/InvoiceSettings/Advanced/InvoiceSettingsAdvanced'
import InvoiceSettingsDebtCollection from '@pages/SettingsPage/sections/InvoiceSettings/DebtCollection/InvoiceSettingsDebtCollection'
import InvoiceSettingsGeneral from '@pages/SettingsPage/sections/InvoiceSettings/General/InvoiceSettingsGeneral'
import { HolviIntegrationSettings } from '@pages/SettingsPage/sections/HolviIntegrationSettings'
import { SalaxySettings } from '@pages/SettingsPage/sections/SalaxySettings'
import { PaymentsSettings } from '@pages/SettingsPage/sections/PaymentsSettings'
import TagSettings from '@pages/SettingsPage/sections/TagsSettings/TagSettings'
import ChangeLogSettings from '@pages/SettingsPage/sections/ChangeLogSettings/ChangeLogSettings'
import VerohallintoSettings from '@pages/SettingsPage/sections/VatReportSettings/VerohallintoSettings'
import ElsterSettings from '@pages/SettingsPage/sections/VatReportSettings/ElsterSettings'
import { ExpenseClaimRatesSettings } from '@pages/SettingsPage/sections/ExpenseClaimRatesSettings/ExpenseClaimRatesSettings'
import {
  DatevIntegrationSettings,
  DatevSettingsNavItem
} from '@pages/SettingsPage/sections/DatevIntegrationSettings'
import {
  MenuItemLabel,
  SettingsPageSubSelector,
  SettingsSubNavItem
} from './components/SettingsSubNavItem'
import classNames from 'classnames'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { NocfoPlan } from '@constants'
import {
  FaAward,
  FaAddressCard,
  FaBalanceScale,
  FaBookOpen,
  FaCalculator,
  FaCar,
  FaCaretRight,
  FaCreditCard,
  FaCog,
  FaExchangeAlt,
  FaExclamationTriangle,
  FaFileExport,
  FaFileInvoiceDollar,
  FaGlobeEurope,
  FaHashtag,
  FaHistory,
  FaLandmark,
  FaMagic,
  FaMask,
  FaPlug,
  FaTag,
  FaUniversity,
  FaUserFriends,
  FaUsers,
  FaSlidersH,
  FaTachometerAlt
} from 'react-icons/fa'
import styled from 'styled-components'
import {
  BusinessDangerZoneSettings,
  BusinessSettings,
  EInvoicingSettings,
  IntegrationSettings,
  LucaUsageSettings,
  PeopleSettings,
  PlanSettings,
  ReferralSettings
} from './sections'
import { useLucaUsageVisibility } from '@utils/lucaUsageVisibility'
import { isLucaPaygBlockedByMissingPaymentMethod } from '@utils/lucaPaygBilling'
import { LucaPaygPaymentWarningIcon } from '@root/containers/Stripe/LucaUsageSection/LucaPaygMissingPaymentMethod'
import LucaSettingsPage, { LucaConnectionsPage } from '@pages/LucaSettingsPage'

enum SettingsPageSections {
  INVOICING_GENERAL = 'invoicing-general',
  INVOICING_DEBT_COLLECTION = 'invoicing-debt-collection',
  INVOICING_ADVANCED = 'invoicing-advanced',
  AUTOMATION_RULES = 'automation-rules',
  AUTOMATION_VAT_SETTINGS = 'automation-vat-settings',
  AUTOMATION_OPENING_BALANCE = 'automation-opening-balance',
  AUTOMATION_DEFAULT_ACCOUNTS = 'automation-default-accounts',
  EINVOICING = 'einvoicing',
  INTEGRATIONS = 'integrations',
  HOLVI = 'holvi',
  PALKKAUS = 'palkkaus',
  PAYMENTS = 'payments',
  DATA_EXPORT = 'data-export',
  SETTINGS = 'settings',
  GENERAL_DANGER_ZONE = 'general-danger-zone',
  PEOPLE = 'people',
  EXPENSE_CLAIMS = 'expense-claims',
  REFERRAL = 'referral',
  PLAN = 'plan',
  PLAN_PACKAGES = 'plan-packages',
  PLAN_MANAGEMENT = 'plan-management',
  LUCA_USAGE = 'luca-usage',
  LUCA_CONNECTIONS = 'luca-connections',
  LUCA = 'luca',
  TAGS = 'tags',
  CHANGE_LOG = 'change-log',
  VEROHALLINTO = 'verohallinto',
  ELSTER = 'elster',
  DATEV = 'datev'
}

const legacySectionMapping: Record<string, SettingsPageSections> = {
  'invoicing:general': SettingsPageSections.INVOICING_GENERAL,
  'invoicing:automaticDebtCollection': SettingsPageSections.INVOICING_DEBT_COLLECTION,
  'invoicing:advanced': SettingsPageSections.INVOICING_ADVANCED,
  'automation:automation': SettingsPageSections.AUTOMATION_RULES,
  'automation:accounts': SettingsPageSections.AUTOMATION_DEFAULT_ACCOUNTS
}

const groupSectionIds = {
  general: new Set<string>([
    SettingsPageSections.SETTINGS,
    SettingsPageSections.PEOPLE,
    SettingsPageSections.EXPENSE_CLAIMS,
    SettingsPageSections.GENERAL_DANGER_ZONE
  ]),
  invoicing: new Set<string>([
    SettingsPageSections.INVOICING_GENERAL,
    SettingsPageSections.INVOICING_DEBT_COLLECTION,
    SettingsPageSections.INVOICING_ADVANCED,
    SettingsPageSections.EINVOICING
  ]),
  automation: new Set<string>([
    SettingsPageSections.AUTOMATION_RULES,
    SettingsPageSections.AUTOMATION_VAT_SETTINGS,
    SettingsPageSections.AUTOMATION_OPENING_BALANCE,
    SettingsPageSections.AUTOMATION_DEFAULT_ACCOUNTS,
    SettingsPageSections.PALKKAUS,
    SettingsPageSections.TAGS
  ]),
  bank: new Set<string>([
    SettingsPageSections.INTEGRATIONS,
    SettingsPageSections.HOLVI,
    SettingsPageSections.PAYMENTS,
    SettingsPageSections.VEROHALLINTO,
    SettingsPageSections.ELSTER,
    SettingsPageSections.DATEV
  ]),
  plan: new Set<string>([SettingsPageSections.PLAN_PACKAGES, SettingsPageSections.PLAN_MANAGEMENT]),
  luca: new Set<string>([
    SettingsPageSections.LUCA,
    SettingsPageSections.LUCA_CONNECTIONS,
    SettingsPageSections.LUCA_USAGE
  ])
}

export const SettingsPage: React.FC = () => {
  const [t] = useTranslation()
  const history = useHistory()
  const [section, setSection] = useQueryParam('page')
  const [tab] = useQueryParam('tab')
  const [panel] = useQueryParam('panel')
  const [ruleId] = useQueryParam<number>('id', value => {
    const parsed = parseInt(value, 10)
    return Number.isFinite(parsed) ? parsed : undefined
  })
  const [, setQueryParams] = useQueryParams()
  const [isGeneralGroupOpen, setIsGeneralGroupOpen] = React.useState(false)
  const [isInvoicingGroupOpen, setIsInvoicingGroupOpen] = React.useState(false)
  const [isAutomationGroupOpen, setIsAutomationGroupOpen] = React.useState(false)
  const [isBankGroupOpen, setIsBankGroupOpen] = React.useState(false)
  const [isPlanGroupOpen, setIsPlanGroupOpen] = React.useState(false)
  const [isLucaGroupOpen, setIsLucaGroupOpen] = React.useState(false)
  const [packagesModalOpen, setPackagesModalOpen] = React.useState(false)
  const { slug, data: business } = useBusinessContext()
  const is_billable = isBillableBusiness(business)
  const isFreePlan = business?.subscription_plan === NocfoPlan.FREE
  const isStripeBillingLocked = business?.billing_lock_reason === 'stripe_unpaid'
  const { showLucaUsage } = useLucaUsageVisibility()
  const lucaPaygMissingPaymentMethod =
    showLucaUsage && isLucaPaygBlockedByMissingPaymentMethod(business)
  const canViewLucaSettings = usePermission(PermissionSet.ADMIN)
  const lucaPermissionsLoaded = usePermissionsLoaded()
  const flagsLoaded = useFlagsLoaded()
  const workflowsFlag = useFlag('workflows')
  const workflowsHubEnabled = flagsLoaded && workflowsFlag.enabled
  const closeChangeLog = React.useCallback(() => {
    setQueryParams({ page: undefined, tab: undefined, panel: undefined })
  }, [setQueryParams])

  React.useEffect(() => {
    if (!section) {
      return
    }

    const legacyKey = `${section}:${tab || ''}`
    const mappedSection = legacySectionMapping[legacyKey]

    if (mappedSection) {
      if (workflowsHubEnabled && mappedSection === SettingsPageSections.AUTOMATION_RULES) {
        history.replace(getWorkflowsPageUrl(slug, { tab: 'rules', id: ruleId }))
        return
      }
      setQueryParams({ page: mappedSection, tab: undefined })
      return
    }

    if (section === 'invoicing') {
      setQueryParams({ page: SettingsPageSections.INVOICING_GENERAL, tab: undefined })
      return
    }

    if (section === 'automation') {
      if (workflowsHubEnabled) {
        history.replace(getWorkflowsPageUrl(slug, { tab: 'rules', id: ruleId }))
        return
      }
      setQueryParams({ page: SettingsPageSections.AUTOMATION_RULES, tab: undefined })
      return
    }

    if (workflowsHubEnabled && section === SettingsPageSections.AUTOMATION_RULES) {
      history.replace(getWorkflowsPageUrl(slug, { tab: 'rules', id: ruleId }))
      return
    }

    // Keep plan management and change log reachable while billing is locked
    // (audit history deep links should not bounce to billing).
    if (
      isStripeBillingLocked &&
      section !== SettingsPageSections.PLAN_MANAGEMENT &&
      section !== SettingsPageSections.CHANGE_LOG
    ) {
      setQueryParams({ page: SettingsPageSections.PLAN_MANAGEMENT, tab: undefined })
      return
    }

    if (section === SettingsPageSections.LUCA && tab === 'connectors') {
      setQueryParams({ page: SettingsPageSections.LUCA_CONNECTIONS, tab: undefined })
      return
    }

    if (section === SettingsPageSections.LUCA && lucaPermissionsLoaded && !canViewLucaSettings) {
      setQueryParams({ page: SettingsPageSections.LUCA_CONNECTIONS, tab: undefined })
      return
    }

    if (section === SettingsPageSections.PLAN) {
      if (!business) {
        return
      }

      if (isFreePlan) {
        setPackagesModalOpen(true)
        setQueryParams({ page: SettingsPageSections.PLAN_MANAGEMENT, tab: undefined })
        return
      }

      setQueryParams({
        page: SettingsPageSections.PLAN_MANAGEMENT,
        tab: undefined
      })
      return
    }

    if (section === SettingsPageSections.PLAN_PACKAGES && business) {
      if (!isFreePlan) {
        setQueryParams({ page: SettingsPageSections.PLAN_MANAGEMENT, tab: undefined })
        return
      }

      setPackagesModalOpen(true)
      setQueryParams({ page: SettingsPageSections.PLAN_MANAGEMENT, tab: undefined })
    }
  }, [
    business,
    history,
    isFreePlan,
    isStripeBillingLocked,
    ruleId,
    section,
    setQueryParams,
    slug,
    tab,
    canViewLucaSettings,
    lucaPermissionsLoaded,
    workflowsHubEnabled
  ])

  React.useEffect(() => {
    const panelAllowed =
      section === SettingsPageSections.PLAN_MANAGEMENT ||
      (section === SettingsPageSections.LUCA_USAGE && (panel === 'bundles' || panel === 'payg'))
    if (section && panel && !panelAllowed) {
      setQueryParams({ panel: undefined })
    }
  }, [panel, section, setQueryParams])

  React.useEffect(() => {
    if (groupSectionIds.general.has(section)) {
      setIsGeneralGroupOpen(true)
    }

    if (groupSectionIds.invoicing.has(section)) {
      setIsInvoicingGroupOpen(true)
    }

    if (groupSectionIds.automation.has(section)) {
      setIsAutomationGroupOpen(true)
    }

    if (groupSectionIds.bank.has(section)) {
      setIsBankGroupOpen(true)
    }

    if (groupSectionIds.plan.has(section)) {
      setIsPlanGroupOpen(true)
    }

    if (groupSectionIds.luca.has(section)) {
      setIsLucaGroupOpen(true)
    }
  }, [section])

  const MenuLink = React.memo<{
    icon: React.ReactNode
    name: string
    link: string
    suffix?: React.ReactNode
  }>(({ icon, name, link, suffix }) => (
    <SettingsPageSelector
      href="#"
      onClick={e => {
        setSection(link)
        e.preventDefault()
      }}
      className={classNames({ active: section === link })}
    >
      <span className="icon-wrapper">{icon}</span>
      <span className="item-name">
        <MenuItemLabel>
          {name}
          {suffix}
        </MenuItemLabel>
      </span>
    </SettingsPageSelector>
  ))

  if (isStripeBillingLocked) {
    return (
      <DoubleColumnLayout
        header={t('settings.mainTitle')}
        isRightVisible={true}
        onRightClose={() =>
          setQueryParams({ page: SettingsPageSections.PLAN_MANAGEMENT, tab: undefined })
        }
      >
        <DoubleColumnLayoutColumn innerKey="settings">
          <SettingsPageContent>
            <PermissionBoundary>
              <SettingsPageGroupButton type="button" className="open" aria-expanded={true}>
                <span className="icon-wrapper">
                  <FaAward />
                </span>
                <span className="item-name">{t('settings.sections.plan')}</span>
              </SettingsPageGroupButton>
              <SettingsPageGroup>
                <SettingsPageSubSelector
                  href="#"
                  onClick={e => {
                    setSection(SettingsPageSections.PLAN_MANAGEMENT)
                    e.preventDefault()
                  }}
                  className="active"
                >
                  <span className="icon-wrapper">
                    <FaCreditCard />
                  </span>
                  <span className="item-name">{t('settings.sections.planManagement')}</span>
                </SettingsPageSubSelector>
              </SettingsPageGroup>
            </PermissionBoundary>
          </SettingsPageContent>
        </DoubleColumnLayoutColumn>
        <DoubleColumnLayoutColumn isRight={true} innerKey={SettingsPageSections.PLAN_MANAGEMENT}>
          <PermissionBoundary>{is_billable && <PlanSettings />}</PermissionBoundary>
        </DoubleColumnLayoutColumn>
      </DoubleColumnLayout>
    )
  }

  if (section === SettingsPageSections.CHANGE_LOG) {
    return (
      <SingleColumnLayout header={t('settings.mainTitle')}>
        <PermissionBoundary>
          <ChangeLogSettings onBack={closeChangeLog} />
        </PermissionBoundary>
      </SingleColumnLayout>
    )
  }

  return (
    <>
      <DoubleColumnLayout
        header={t('settings.mainTitle')}
        isRightVisible={Boolean(section)}
        onRightClose={() => setQueryParams({ page: undefined, tab: undefined, panel: undefined })}
      >
        <DoubleColumnLayoutColumn innerKey="settings">
          <SettingsPageContent>
            <PermissionBoundary>
              <SettingsPageGroupButton
                type="button"
                onClick={() => setIsGeneralGroupOpen(previous => !previous)}
                className={classNames({
                  active: groupSectionIds.general.has(section) && !isGeneralGroupOpen,
                  open: isGeneralGroupOpen
                })}
                aria-expanded={isGeneralGroupOpen}
              >
                <span className="icon-wrapper">
                  <FaCog />
                </span>
                <span className="item-name">{t('settings.sections.general')}</span>
                <FaCaretRight className="caret" />
              </SettingsPageGroupButton>

              {isGeneralGroupOpen && (
                <SettingsPageGroup>
                  <SettingsPageSubSelector
                    href="#"
                    onClick={e => {
                      setSection(SettingsPageSections.SETTINGS)
                      e.preventDefault()
                    }}
                    className={classNames({
                      active: section === SettingsPageSections.SETTINGS
                    })}
                  >
                    <span className="icon-wrapper">
                      <FaAddressCard />
                    </span>
                    <span className="item-name">{t('settings.titleInfo')}</span>
                  </SettingsPageSubSelector>

                  <SettingsPageSubSelector
                    href="#"
                    onClick={e => {
                      setSection(SettingsPageSections.PEOPLE)
                      e.preventDefault()
                    }}
                    className={classNames({
                      active: section === SettingsPageSections.PEOPLE
                    })}
                  >
                    <span className="icon-wrapper">
                      <FaUsers />
                    </span>
                    <span className="item-name">{t('settings.sections.people')}</span>
                  </SettingsPageSubSelector>

                  <FeatureWrapper feature="expense_claims">
                    <FlagWrapper flag="expense_claims">
                      <PermissionBoundary required={PermissionSet.ADMIN}>
                        <SettingsSubNavItem
                          link={SettingsPageSections.EXPENSE_CLAIMS}
                          icon={<FaCar />}
                          name={t('settings.sections.expenseClaims')}
                          isActive={section === SettingsPageSections.EXPENSE_CLAIMS}
                          onSelect={setSection}
                        />
                      </PermissionBoundary>
                    </FlagWrapper>
                  </FeatureWrapper>

                  <SettingsPageSubSelector
                    href="#"
                    onClick={e => {
                      setSection(SettingsPageSections.GENERAL_DANGER_ZONE)
                      e.preventDefault()
                    }}
                    className={classNames({
                      active: section === SettingsPageSections.GENERAL_DANGER_ZONE
                    })}
                  >
                    <span className="icon-wrapper">
                      <FaExclamationTriangle />
                    </span>
                    <span className="item-name">{t('settings.sections.businessDeletion')}</span>
                  </SettingsPageSubSelector>
                </SettingsPageGroup>
              )}
            </PermissionBoundary>

            <PermissionBoundary required={PermissionSet.BOOKKEEPING_EDIT}>
              <SettingsPageGroupButton
                type="button"
                onClick={() => setIsAutomationGroupOpen(previous => !previous)}
                className={classNames({
                  active: groupSectionIds.automation.has(section) && !isAutomationGroupOpen,
                  open: isAutomationGroupOpen
                })}
                aria-expanded={isAutomationGroupOpen}
              >
                <span className="icon-wrapper">
                  <FaBookOpen />
                </span>
                <span className="item-name">{t('settings.sections.accountingSettings')}</span>
                <FaCaretRight className="caret" />
              </SettingsPageGroupButton>

              {isAutomationGroupOpen && (
                <SettingsPageGroup>
                  <PermissionBoundary required={PermissionSet.BOOKKEEPING_ADMIN}>
                    {!workflowsHubEnabled && (
                      <SettingsPageSubSelector
                        href="#"
                        onClick={e => {
                          setSection(SettingsPageSections.AUTOMATION_RULES)
                          e.preventDefault()
                        }}
                        className={classNames({
                          active: section === SettingsPageSections.AUTOMATION_RULES
                        })}
                      >
                        <span className="icon-wrapper">
                          <FaMagic />
                        </span>
                        <span className="item-name">{t('settings.sections.automationRules')}</span>
                      </SettingsPageSubSelector>
                    )}

                    <SettingsPageSubSelector
                      href="#"
                      onClick={e => {
                        setSection(SettingsPageSections.AUTOMATION_VAT_SETTINGS)
                        e.preventDefault()
                      }}
                      className={classNames({
                        active: section === SettingsPageSections.AUTOMATION_VAT_SETTINGS
                      })}
                    >
                      <span className="icon-wrapper">
                        <FaMask />
                      </span>
                      <span className="item-name">{t('settings.sections.vatSettings')}</span>
                    </SettingsPageSubSelector>

                    <SettingsPageSubSelector
                      href="#"
                      onClick={e => {
                        setSection(SettingsPageSections.AUTOMATION_OPENING_BALANCE)
                        e.preventDefault()
                      }}
                      className={classNames({
                        active: section === SettingsPageSections.AUTOMATION_OPENING_BALANCE
                      })}
                    >
                      <span className="icon-wrapper">
                        <FaBookOpen />
                      </span>
                      <span className="item-name">{t('settings.sections.openingBalances')}</span>
                    </SettingsPageSubSelector>

                    <SettingsPageSubSelector
                      href="#"
                      onClick={e => {
                        setSection(SettingsPageSections.AUTOMATION_DEFAULT_ACCOUNTS)
                        e.preventDefault()
                      }}
                      className={classNames({
                        active: section === SettingsPageSections.AUTOMATION_DEFAULT_ACCOUNTS
                      })}
                    >
                      <span className="icon-wrapper">
                        <FaCalculator />
                      </span>
                      <span className="item-name">{t('settings.sections.defaultAccounts')}</span>
                    </SettingsPageSubSelector>
                  </PermissionBoundary>

                  <FeatureWrapper feature="salaries">
                    <PermissionBoundary required={PermissionSet.SALARY}>
                      <SettingsPageSubSelector
                        href="#"
                        onClick={e => {
                          setSection(SettingsPageSections.PALKKAUS)
                          e.preventDefault()
                        }}
                        className={classNames({
                          active: section === SettingsPageSections.PALKKAUS
                        })}
                      >
                        <span className="icon-wrapper">
                          <FaCalculator />
                        </span>
                        <span className="item-name">{t('settings.sections.palkkaus')}</span>
                      </SettingsPageSubSelector>
                    </PermissionBoundary>
                  </FeatureWrapper>

                  <PermissionBoundary required={PermissionSet.BOOKKEEPING_EDIT}>
                    <SettingsPageSubSelector
                      href="#"
                      onClick={e => {
                        setSection(SettingsPageSections.TAGS)
                        e.preventDefault()
                      }}
                      className={classNames({
                        active: section === SettingsPageSections.TAGS
                      })}
                    >
                      <span className="icon-wrapper">
                        <FaTag />
                      </span>
                      <span className="item-name">{t('settings.sections.tags')}</span>
                    </SettingsPageSubSelector>
                  </PermissionBoundary>
                </SettingsPageGroup>
              )}
            </PermissionBoundary>

            <PermissionBoundary required={PermissionSet.INVOICING_ADMIN}>
              <FeatureWrapper feature="invoicing">
                <SettingsPageGroupButton
                  type="button"
                  onClick={() => setIsInvoicingGroupOpen(previous => !previous)}
                  className={classNames({
                    active: groupSectionIds.invoicing.has(section) && !isInvoicingGroupOpen,
                    open: isInvoicingGroupOpen
                  })}
                  aria-expanded={isInvoicingGroupOpen}
                >
                  <span className="icon-wrapper">
                    <FaFileInvoiceDollar />
                  </span>
                  <span className="item-name">
                    {t('settings.sections.invoicingAndDebtCollection')}
                  </span>
                  <FaCaretRight className="caret" />
                </SettingsPageGroupButton>

                {isInvoicingGroupOpen && (
                  <SettingsPageGroup>
                    <SettingsPageSubSelector
                      href="#"
                      onClick={e => {
                        setSection(SettingsPageSections.INVOICING_GENERAL)
                        e.preventDefault()
                      }}
                      className={classNames({
                        active: section === SettingsPageSections.INVOICING_GENERAL
                      })}
                    >
                      <span className="icon-wrapper">
                        <FaAddressCard />
                      </span>
                      <span className="item-name">{t('settings.sections.invoicingBasics')}</span>
                    </SettingsPageSubSelector>

                    <SettingsPageSubSelector
                      href="#"
                      onClick={e => {
                        setSection(SettingsPageSections.INVOICING_DEBT_COLLECTION)
                        e.preventDefault()
                      }}
                      className={classNames({
                        active: section === SettingsPageSections.INVOICING_DEBT_COLLECTION
                      })}
                    >
                      <span className="icon-wrapper">
                        <FaBalanceScale />
                      </span>
                      <span className="item-name">
                        {t('settings.sections.paymentRemindersAndCollection')}
                      </span>
                    </SettingsPageSubSelector>

                    <SettingsPageSubSelector
                      href="#"
                      onClick={e => {
                        setSection(SettingsPageSections.INVOICING_ADVANCED)
                        e.preventDefault()
                      }}
                      className={classNames({
                        active: section === SettingsPageSections.INVOICING_ADVANCED
                      })}
                    >
                      <span className="icon-wrapper">
                        <FaHashtag />
                      </span>
                      <span className="item-name">
                        {t('settings.sections.invoicingNumberingAndDefaults')}
                      </span>
                    </SettingsPageSubSelector>

                    <FeatureWrapper feature="einvoicing">
                      <SettingsPageSubSelector
                        href="#"
                        onClick={e => {
                          setSection(SettingsPageSections.EINVOICING)
                          e.preventDefault()
                        }}
                        className={classNames({
                          active: section === SettingsPageSections.EINVOICING
                        })}
                      >
                        <span className="icon-wrapper">
                          <FaGlobeEurope />
                        </span>
                        <span className="item-name">{t('settings.sections.einvoicing')}</span>
                      </SettingsPageSubSelector>
                    </FeatureWrapper>
                  </SettingsPageGroup>
                )}
              </FeatureWrapper>
            </PermissionBoundary>

            <PermissionBoundary
              required={[
                ...PermissionSet.INTEGRATION_ADMIN,
                ...PermissionSet.REPORTING_ADMIN,
                ...PermissionSet.INVOICING_ADMIN
              ]}
            >
              <SettingsPageGroupButton
                type="button"
                onClick={() => setIsBankGroupOpen(previous => !previous)}
                className={classNames({
                  active: groupSectionIds.bank.has(section) && !isBankGroupOpen,
                  open: isBankGroupOpen
                })}
                aria-expanded={isBankGroupOpen}
              >
                <span className="icon-wrapper">
                  <FaExchangeAlt />
                </span>
                <span className="item-name">
                  {t('settings.sections.bankConnectionsAndPayments')}
                </span>
                <FaCaretRight className="caret" />
              </SettingsPageGroupButton>

              {isBankGroupOpen && (
                <SettingsPageGroup>
                  <PermissionBoundary required={PermissionSet.INTEGRATION_ADMIN}>
                    <FeatureWrapper feature="holvi_integration">
                      <SettingsPageSubSelector
                        href="#"
                        onClick={e => {
                          setSection(SettingsPageSections.HOLVI)
                          e.preventDefault()
                        }}
                        className={classNames({
                          active: section === SettingsPageSections.HOLVI
                        })}
                      >
                        <span className="icon-wrapper">
                          <FaUniversity />
                        </span>
                        <span className="item-name">{t('settings.sections.holviIntegration')}</span>
                      </SettingsPageSubSelector>
                    </FeatureWrapper>
                    <DatevSettingsNavItem
                      isActive={section === SettingsPageSections.DATEV}
                      onSelect={() => setSection(SettingsPageSections.DATEV)}
                    />
                  </PermissionBoundary>

                  <PermissionBoundary required={PermissionSet.INTEGRATION_ADMIN}>
                    <SettingsPageSubSelector
                      href="#"
                      onClick={e => {
                        setSection(SettingsPageSections.INTEGRATIONS)
                        e.preventDefault()
                      }}
                      className={classNames({
                        active: section === SettingsPageSections.INTEGRATIONS
                      })}
                    >
                      <span className="icon-wrapper">
                        <FaExchangeAlt />
                      </span>
                      <span className="item-name">{t('settings.sections.integrations')}</span>
                    </SettingsPageSubSelector>
                  </PermissionBoundary>

                  <PermissionBoundary required={PermissionSet.INVOICING_ADMIN}>
                    <SettingsPageSubSelector
                      href="#"
                      onClick={e => {
                        setSection(SettingsPageSections.PAYMENTS)
                        e.preventDefault()
                      }}
                      className={classNames({
                        active: section === SettingsPageSections.PAYMENTS
                      })}
                    >
                      <span className="icon-wrapper">
                        <FaCreditCard />
                      </span>
                      <span className="item-name">{t('settings.sections.payments')}</span>
                    </SettingsPageSubSelector>
                  </PermissionBoundary>

                  <PermissionBoundary required={PermissionSet.REPORTING_ADMIN}>
                    <FeatureWrapper feature="vat_external_reporting">
                      <FeatureWrapper feature="omavero">
                        <SettingsSubNavItem
                          link={SettingsPageSections.VEROHALLINTO}
                          icon={<FaLandmark />}
                          name={t('settings.sections.verohallinto')}
                          isActive={section === SettingsPageSections.VEROHALLINTO}
                          onSelect={setSection}
                        />
                      </FeatureWrapper>
                      <FeatureWrapper feature="elster">
                        <SettingsSubNavItem
                          link={SettingsPageSections.ELSTER}
                          icon={<ElsterLogo variant="star" height={16} />}
                          name={t('settings.sections.elster')}
                          isActive={section === SettingsPageSections.ELSTER}
                          onSelect={setSection}
                          suffix={
                            <BetaPill
                              label={t('general.beta.label')}
                              tooltip={t('general.beta.tooltip')}
                            />
                          }
                        />
                      </FeatureWrapper>
                    </FeatureWrapper>
                  </PermissionBoundary>
                </SettingsPageGroup>
              )}
            </PermissionBoundary>

            <PermissionBoundary>
              {is_billable && (
                <>
                  <SettingsPageGroupButton
                    type="button"
                    onClick={() => setIsPlanGroupOpen(previous => !previous)}
                    className={classNames({
                      active: groupSectionIds.plan.has(section) && !isPlanGroupOpen,
                      open: isPlanGroupOpen
                    })}
                    aria-expanded={isPlanGroupOpen}
                  >
                    <span className="icon-wrapper">
                      <FaAward />
                    </span>
                    <span className="item-name">{t('settings.sections.plan')}</span>
                    <FaCaretRight className="caret" />
                  </SettingsPageGroupButton>

                  {isPlanGroupOpen && (
                    <SettingsPageGroup>
                      {isFreePlan && (
                        <SettingsPageSubSelector
                          href="#"
                          onClick={e => {
                            setSection(SettingsPageSections.PLAN_MANAGEMENT)
                            setPackagesModalOpen(true)
                            e.preventDefault()
                          }}
                          className={classNames({
                            active: packagesModalOpen
                          })}
                        >
                          <span className="icon-wrapper">
                            <FaAward />
                          </span>
                          <span className="item-name">{t('settings.sections.planPackages')}</span>
                        </SettingsPageSubSelector>
                      )}

                      <SettingsPageSubSelector
                        href="#"
                        onClick={e => {
                          setSection(SettingsPageSections.PLAN_MANAGEMENT)
                          e.preventDefault()
                        }}
                        className={classNames({
                          active: section === SettingsPageSections.PLAN_MANAGEMENT
                        })}
                      >
                        <span className="icon-wrapper">
                          <FaCreditCard />
                        </span>
                        <span className="item-name">{t('settings.sections.planManagement')}</span>
                      </SettingsPageSubSelector>
                    </SettingsPageGroup>
                  )}
                </>
              )}
            </PermissionBoundary>

            <SettingsPageGroupButton
              type="button"
              onClick={() => setIsLucaGroupOpen(previous => !previous)}
              className={classNames({
                active: groupSectionIds.luca.has(section) && !isLucaGroupOpen,
                open: isLucaGroupOpen
              })}
              aria-expanded={isLucaGroupOpen}
            >
              <span className="icon-wrapper">
                <LucaIcon />
              </span>
              <span className="item-name">{t('settings.sections.luca')}</span>
              {lucaPaygMissingPaymentMethod && <LucaPaygPaymentWarningIcon />}
              <FaCaretRight className="caret" />
            </SettingsPageGroupButton>

            {isLucaGroupOpen && (
              <SettingsPageGroup>
                {canViewLucaSettings && (
                  <SettingsPageSubSelector
                    href="#"
                    onClick={e => {
                      setSection(SettingsPageSections.LUCA)
                      e.preventDefault()
                    }}
                    className={classNames({
                      active: section === SettingsPageSections.LUCA
                    })}
                  >
                    <span className="icon-wrapper">
                      <FaSlidersH />
                    </span>
                    <span className="item-name">{t('settings.sections.lucaSettings')}</span>
                  </SettingsPageSubSelector>
                )}

                <SettingsPageSubSelector
                  href="#"
                  onClick={e => {
                    setSection(SettingsPageSections.LUCA_CONNECTIONS)
                    e.preventDefault()
                  }}
                  className={classNames({
                    active: section === SettingsPageSections.LUCA_CONNECTIONS
                  })}
                >
                  <span className="icon-wrapper">
                    <FaPlug />
                  </span>
                  <span className="item-name">{t('settings.sections.lucaConnections')}</span>
                </SettingsPageSubSelector>

                {showLucaUsage && (
                  <SettingsPageSubSelector
                    href="#"
                    onClick={e => {
                      setSection(SettingsPageSections.LUCA_USAGE)
                      e.preventDefault()
                    }}
                    className={classNames({
                      active: section === SettingsPageSections.LUCA_USAGE
                    })}
                  >
                    <span className="icon-wrapper">
                      <FaTachometerAlt />
                    </span>
                    <span className="item-name">
                      <MenuItemLabel>
                        {t('settings.sections.lucaUsage')}
                        {lucaPaygMissingPaymentMethod && <LucaPaygPaymentWarningIcon />}
                      </MenuItemLabel>
                    </span>
                  </SettingsPageSubSelector>
                )}
              </SettingsPageGroup>
            )}

            <PermissionBoundary>
              <MenuLink
                icon={<FaUserFriends />}
                name={t('settings.sections.referral')}
                link={SettingsPageSections.REFERRAL}
              />
            </PermissionBoundary>

            <PermissionBoundary>
              <MenuLink
                icon={<FaFileExport />}
                name={t('settings.sections.dataExport')}
                link={SettingsPageSections.DATA_EXPORT}
              />
            </PermissionBoundary>

            <PermissionBoundary>
              <MenuLink
                icon={<FaHistory />}
                name={t('settings.sections.changeLog')}
                link={SettingsPageSections.CHANGE_LOG}
                suffix={
                  <BetaPill label={t('general.beta.label')} tooltip={t('general.beta.tooltip')} />
                }
              />
            </PermissionBoundary>
          </SettingsPageContent>
        </DoubleColumnLayoutColumn>

        <DoubleColumnLayoutColumn isRight={true} innerKey={section}>
          <PermissionBoundary required={PermissionSet.INVOICING_ADMIN}>
            {section === SettingsPageSections.EINVOICING && (
              <FeatureWrapper feature="einvoicing">
                <EInvoicingSettings />
              </FeatureWrapper>
            )}

            <FeatureWrapper feature="invoicing">
              {section === SettingsPageSections.INVOICING_GENERAL && <InvoiceSettingsGeneral />}
              {section === SettingsPageSections.INVOICING_DEBT_COLLECTION && (
                <InvoiceSettingsDebtCollection />
              )}
              {section === SettingsPageSections.INVOICING_ADVANCED && <InvoiceSettingsAdvanced />}
              {section === SettingsPageSections.PAYMENTS && <PaymentsSettings />}
            </FeatureWrapper>
          </PermissionBoundary>

          <PermissionBoundary required={PermissionSet.BOOKKEEPING_ADMIN}>
            {!workflowsHubEnabled && section === SettingsPageSections.AUTOMATION_RULES && (
              <AccountingRuleSettings />
            )}
            {section === SettingsPageSections.AUTOMATION_VAT_SETTINGS && <VatSettings />}
            {section === SettingsPageSections.AUTOMATION_OPENING_BALANCE && (
              <OpeningBalanceSettings />
            )}
            {section === SettingsPageSections.AUTOMATION_DEFAULT_ACCOUNTS && (
              <DefaultAccountSettings />
            )}
          </PermissionBoundary>

          <PermissionBoundary required={PermissionSet.REPORTING_ADMIN}>
            <VatReportingSection
              targetSection={SettingsPageSections.VEROHALLINTO}
              countryFeature="omavero"
              currentSection={section}
            >
              <VerohallintoSettings />
            </VatReportingSection>
            <VatReportingSection
              targetSection={SettingsPageSections.ELSTER}
              countryFeature="elster"
              currentSection={section}
            >
              <ElsterSettings />
            </VatReportingSection>
          </PermissionBoundary>

          <PermissionBoundary required={PermissionSet.INTEGRATION_ADMIN}>
            {section === SettingsPageSections.INTEGRATIONS && <IntegrationSettings />}
            {section === SettingsPageSections.HOLVI && (
              <FeatureWrapper feature="holvi_integration">
                <HolviIntegrationSettings />
              </FeatureWrapper>
            )}
            {section === SettingsPageSections.DATEV && <DatevIntegrationSettings />}
          </PermissionBoundary>

          <FeatureWrapper feature="salaries">
            {section === SettingsPageSections.PALKKAUS && (
              <PermissionBoundary required={PermissionSet.SALARY}>
                <SalaxySettings />
              </PermissionBoundary>
            )}
          </FeatureWrapper>

          <PermissionBoundary>
            {section === SettingsPageSections.REFERRAL && <ReferralSettings />}
          </PermissionBoundary>

          <PermissionBoundary>
            {section === SettingsPageSections.LUCA_USAGE && showLucaUsage && <LucaUsageSettings />}
          </PermissionBoundary>

          {section === SettingsPageSections.LUCA && canViewLucaSettings && <LucaSettingsPage />}
          {section === SettingsPageSections.LUCA_CONNECTIONS && <LucaConnectionsPage />}

          <PermissionBoundary>
            {section === SettingsPageSections.SETTINGS && <BusinessSettings />}
            {section === SettingsPageSections.GENERAL_DANGER_ZONE && <BusinessDangerZoneSettings />}
            {section === SettingsPageSections.PEOPLE && <PeopleSettings />}
            <FeatureWrapper feature="expense_claims">
              <FlagWrapper flag="expense_claims">
                <PermissionBoundary required={PermissionSet.ADMIN}>
                  {section === SettingsPageSections.EXPENSE_CLAIMS && <ExpenseClaimRatesSettings />}
                </PermissionBoundary>
              </FlagWrapper>
            </FeatureWrapper>
            {section === SettingsPageSections.DATA_EXPORT && <DataExport />}

            {is_billable && section === SettingsPageSections.PLAN_MANAGEMENT && <PlanSettings />}
          </PermissionBoundary>

          <PermissionBoundary required={PermissionSet.BOOKKEEPING_EDIT}>
            {section === SettingsPageSections.TAGS && <TagSettings />}
          </PermissionBoundary>
        </DoubleColumnLayoutColumn>
      </DoubleColumnLayout>
      {is_billable && (
        <CustomPricingTableModal
          isVisible={packagesModalOpen}
          handleOnClose={() => {
            setPackagesModalOpen(false)
            if (section === SettingsPageSections.PLAN_PACKAGES) {
              setQueryParams({ page: SettingsPageSections.PLAN_MANAGEMENT, tab: undefined })
            }
          }}
        />
      )}
    </>
  )
}

const SettingsPageContent = styled.div`
  height: 100%;
  overflow: auto;
  padding: ${({ theme }) => theme.spacing.md}rem;
  background: ${({ theme }) => theme.colors.neutralGray};
`

const SettingsPageSelector = styled.a`
  padding: ${({ theme }) => theme.spacing.md}rem;
  display: flex;
  color: ${({ theme }) => theme.colors.neutralBlack};
  background: white;
  align-items: center;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  border: 1px solid transparent;
  margin-bottom: 1px;
  outline: none;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.defaultHover};
    border-color: ${({ theme }) => theme.colors.neutralGray};
    color: ${({ theme }) => theme.colors.neutralBlack};
    text-decoration: none;
  }

  &.active {
    background: white;
    color: ${({ theme }) => theme.colors.primary};
  }

  &:hover .icon-wrapper svg,
  &:hover .icon-wrapper img {
    color: ${({ theme }) => theme.colors.iconGrayDark};
  }

  &.active .icon-wrapper svg,
  &.active .icon-wrapper img {
    color: ${({ theme }) => theme.colors.primary};
  }

  .icon-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.25rem;
  }

  .icon-wrapper svg,
  .icon-wrapper img {
    font-size: ${({ theme }) => theme.iconSize.xs}rem;
    width: 1rem;
    height: 1rem;
    color: ${({ theme }) => theme.colors.iconGrayDark};
  }

  .item-name {
    flex: 1;
  }
`

const SettingsPageGroupButton = styled.button`
  padding: ${({ theme }) => theme.spacing.md}rem;
  display: flex;
  width: 100%;
  border: 1px solid transparent;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  margin-bottom: 1px;
  color: ${({ theme }) => theme.colors.neutralBlack};
  background: white;
  align-items: center;
  cursor: pointer;
  font: inherit;
  text-align: left;
  outline: none;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.defaultHover};
    border-color: ${({ theme }) => theme.colors.neutralGray};
    color: ${({ theme }) => theme.colors.neutralBlack};
  }

  &:focus,
  &:focus-visible {
    outline: none;
    box-shadow: none;
  }

  &.active {
    background: white;
    color: ${({ theme }) => theme.colors.primary};
  }

  &:hover .icon-wrapper svg,
  &:hover .icon-wrapper img {
    color: ${({ theme }) => theme.colors.iconGrayDark};
  }

  &.active .icon-wrapper svg,
  &.active .icon-wrapper img {
    color: ${({ theme }) => theme.colors.primary};
  }

  .icon-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.25rem;
  }

  .icon-wrapper svg {
    font-size: ${({ theme }) => theme.iconSize.xs}rem;
    width: 1rem;
    height: 1rem;
    color: ${({ theme }) => theme.colors.iconGrayDark};
  }

  .item-name {
    flex: 1;
    text-align: left;
  }

  .caret {
    transition: transform 0.2s ease;
  }

  &.open .caret {
    transform: rotate(90deg);
  }
`

const SettingsPageGroup = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: ${({ theme }) => theme.spacing.xs}rem;
  padding-bottom: ${({ theme }) => theme.spacing.xs}rem;
  padding-left: ${({ theme }) => theme.spacing.lg}rem;
  gap: 1px;
`

const VatReportingSection: React.FC<{
  targetSection: SettingsPageSections
  countryFeature: 'omavero' | 'elster'
  currentSection?: string
  children: React.ReactNode
}> = ({ targetSection, countryFeature, currentSection, children }) =>
  currentSection === targetSection ? (
    <FeatureWrapper feature="vat_external_reporting">
      <FeatureWrapper feature={countryFeature}>{children}</FeatureWrapper>
    </FeatureWrapper>
  ) : null
